import { useEffect, useRef, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { useUser, useAuth, useAuthContext } from "@trillion/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import { Label } from "@trillion/ui/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardForm,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import { Combobox, type ComboboxOption } from "../components/combobox";
import { AvatarUploader } from "../components/avatar-uploader";
import { ConnectedAgentsSection } from "../components/connected-agents-section";
import { MfaSection } from "../components/mfa-section";
import { ChangeEmailSection } from "../components/change-email-section";
import { ResendVerificationSection } from "../components/resend-verification-section";
import {
  passportApi,
  type Account,
  type GeoCountry,
  type GeoState,
} from "../lib/passport-api";

interface FormState {
  first_name: string;
  last_name: string;
  address_line1: string;
  address_line2: string;
  city: string;
  region: string;
  postal_code: string;
  country: string;
}

const EMPTY: FormState = {
  first_name: "",
  last_name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  region: "",
  postal_code: "",
  country: "",
};

function toForm(p: Account): FormState {
  return {
    first_name: p.first_name ?? "",
    last_name: p.last_name ?? "",
    address_line1: p.address_line1 ?? "",
    address_line2: p.address_line2 ?? "",
    city: p.city ?? "",
    region: p.region ?? "",
    postal_code: p.postal_code ?? "",
    country: p.country ?? "",
  };
}

const clean = (v: string): string | null => (v.trim() === "" ? null : v.trim());

function initials(first: string, last: string, email: string): string {
  const combined = `${first.trim().charAt(0)}${last.trim().charAt(0)}`.toUpperCase();
  return combined || email.charAt(0).toUpperCase() || "?";
}

export function AccountPage() {
  const user = useUser();
  const { isLoading } = useAuthContext();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [countries, setCountries] = useState<GeoCountry[]>([]);
  const [states, setStates] = useState<GeoState[]>([]);
  const [loading, setLoading] = useState(true);
  // null = the API didn't return the field (older API) → render nothing, the safe default.
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  // The country whose states we most recently requested — guards against a slow response
  // for a previously-selected country overwriting the current selection's states.
  const latestCountry = useRef<string>("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [data, countryList] = await Promise.all([
          passportApi.account.get(),
          passportApi.geo.countries(),
        ]);
        if (!active) return;
        const f = toForm(data);
        setForm(f);
        setAvatarUrl(data.avatar_url ?? null);
        setEmailVerified(data.email_verified ?? null);
        setCountries(countryList);
        if (f.country) {
          latestCountry.current = f.country;
          const st = await passportApi.geo.states(f.country);
          if (active && latestCountry.current === f.country) setStates(st);
        }
      } catch (e) {
        if (active) toast.error(e instanceof Error ? e.message : "Failed to load account");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  function update<K extends keyof FormState>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCountryChange(iso2: string) {
    setForm((prev) => ({ ...prev, country: iso2, region: "" }));
    setStates([]);
    latestCountry.current = iso2;
    try {
      const st = await passportApi.geo.states(iso2);
      // Ignore a stale response if the user has since switched countries.
      if (latestCountry.current === iso2) setStates(st);
    } catch {
      if (latestCountry.current === iso2) setStates([]);
    }
  }

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    // first/last are the canonical name and required (server derives display_name from them).
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("First and last name are required.");
      return;
    }
    setSaving(true);
    try {
      const payload: Account = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        address_line1: clean(form.address_line1),
        address_line2: clean(form.address_line2),
        city: clean(form.city),
        region: clean(form.region),
        postal_code: clean(form.postal_code),
        country: clean(form.country),
      };
      const updated = await passportApi.account.update(payload);
      setForm(toForm(updated));
      toast.success("Account updated.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  if (isLoading || !user || loading) {
    return (
      <main className="flex min-h-screen items-center justify-center p-4">
        <p className="text-muted-foreground">Loading…</p>
      </main>
    );
  }

  const countryOptions: ComboboxOption[] = countries.map((c) => ({
    value: c.iso2,
    label: c.name,
  }));
  const stateOptions: ComboboxOption[] = states.map((s) => ({
    value: s.code ?? s.name,
    label: s.name,
  }));

  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold">Account</CardTitle>
            <CardDescription>Your Trillion Passport account</CardDescription>
          </CardHeader>
          <CardForm onSubmit={handleSave}>
            <CardContent className="space-y-4">
              <AvatarUploader
                currentUrl={avatarUrl}
                fallback={initials(form.first_name, form.last_name, user.email)}
                onUploaded={setAvatarUrl}
              />

              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Email</p>
                <p className="text-sm">{user.email}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="first_name">First name</Label>
                  <Input id="first_name" required maxLength={100} value={form.first_name} onChange={(e) => update("first_name", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="last_name">Last name</Label>
                  <Input id="last_name" required maxLength={100} value={form.last_name} onChange={(e) => update("last_name", e.target.value)} />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="address_line1">Address line 1</Label>
                <Input id="address_line1" maxLength={200} value={form.address_line1} onChange={(e) => update("address_line1", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="address_line2">Address line 2</Label>
                <Input id="address_line2" maxLength={200} value={form.address_line2} onChange={(e) => update("address_line2", e.target.value)} />
              </div>

              <div className="space-y-1">
                <Label htmlFor="country">Country</Label>
                <Combobox
                  id="country"
                  options={countryOptions}
                  value={form.country}
                  onChange={handleCountryChange}
                  placeholder="Select a country"
                  searchPlaceholder="Search countries…"
                  emptyText="No country found."
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="region">State / Region</Label>
                  {stateOptions.length > 0 ? (
                    <Combobox
                      id="region"
                      options={stateOptions}
                      value={form.region}
                      onChange={(v) => update("region", v)}
                      placeholder="Select"
                      searchPlaceholder="Search…"
                      emptyText="No match."
                    />
                  ) : (
                    <Input id="region" maxLength={100} value={form.region} onChange={(e) => update("region", e.target.value)} />
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="city">City</Label>
                  <Input id="city" maxLength={100} value={form.city} onChange={(e) => update("city", e.target.value)} />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="postal_code">Postal code</Label>
                <Input id="postal_code" maxLength={20} value={form.postal_code} onChange={(e) => update("postal_code", e.target.value)} />
              </div>

            </CardContent>
            <CardFooter className="flex flex-col gap-2">
              <Button type="submit" className="w-full" disabled={saving}>
                {saving ? "Saving…" : "Save changes"}
              </Button>
              <Button type="button" variant="outline" className="w-full" onClick={handleLogout}>
                Sign out
              </Button>
            </CardFooter>
          </CardForm>
        </Card>

        {emailVerified === false && <ResendVerificationSection email={user.email} />}

        <ChangeEmailSection currentEmail={user.email} />

        <MfaSection />

        <ConnectedAgentsSection key={user.id} userId={user.id} />
      </div>
    </main>
  );
}
